import {pathOr, propEq, find, whereEq, last, pipe, propOr, pickAll} from 'ramda'

import Settings from 'Settings'

import {
  BOOK_OFFER_FINALIZATION,
  BOOK_OFFER_FINALIZATION_DONE,
  BOOK_OFFER_FINALIZATION_PENDING
} from './actions/bookOfferFinalization'

import {ActionLinkTypes} from '@daedalus/core/src/offer/types/actionLink'
import buildUrl from 'utils/buildUrl'
import {decorateOfferWithPayAtProperty} from 'business/offer'

import {StoreStateType} from 'modules'
import {bookOfferCreation, bookOfferCreationPending} from './slice'
import {BookingStatus} from '@daedalus/core/src/booking/types/BookingStatus'

export const buildBookingManagementLink = (
  searchParams: Record<string, unknown>
) => {
  return buildUrl(
    Settings.get('REACT_APP_BOOKING_MANAGEMENT_ENVIRONMENT'),
    searchParams
  )
}

export const bookingResponse = (state: StoreStateType) =>
  state?.book?.booking?.response

export const getIsSuccessFullyBooked = (state: StoreStateType) => {
  const bookingStatusConfirmed = whereEq({status: BookingStatus.CONFIRMED})
  return bookingStatusConfirmed(state?.book?.booking)
}

export const getBookingModuleStatus = (state: StoreStateType) =>
  state?.book?.offerBookState

export const getConfirmationId = (state: StoreStateType) =>
  pathOr([], ['book', 'booking', 'response', 'providerConfirmationId'], state)

export const getBookingId = (state: StoreStateType) =>
  pathOr(undefined, ['book', 'booking', 'id'], state)

export const getBookingStatus = (state: StoreStateType) =>
  pathOr(undefined, ['book', 'booking', 'status'], state)

export const getBillingInfo = (state: StoreStateType) =>
  pathOr('', ['book', 'booking', 'request', 'payment', 'billing'], state)

export const getRooms = (state: StoreStateType) =>
  pathOr([], ['book', 'booking', 'response', 'rooms'], state)

export const getRoomByConfirmationId = (
  state: StoreStateType,
  confirmationId: string
) => {
  return pipe(getRooms, find(propEq('confirmationId', confirmationId)))(state)
}

export const getLastError = (state: StoreStateType) => last(state.book.errors)

export const isPending = (state: StoreStateType) => {
  const pendingStatuses = new Set([
    bookOfferCreation.type,
    bookOfferCreationPending.type,
    BOOK_OFFER_FINALIZATION_PENDING
  ])

  return pendingStatuses.has(state.book.offerBookState)
}

export const getIsFinalizingBooking = (state: StoreStateType) => {
  const pendingStatuses = new Set([
    BOOK_OFFER_FINALIZATION,
    BOOK_OFFER_FINALIZATION_PENDING,
    BOOK_OFFER_FINALIZATION_DONE
  ])

  return pendingStatuses.has(state.book.offerBookState)
}

export const getOfferBookState = (state: StoreStateType) =>
  state?.book?.offerBookState

export const getOfferInfo = (state: StoreStateType) => {
  return decorateOfferWithPayAtProperty(
    pickAll(
      [
        'refundable',
        'prices',
        'merchantOfRecord',
        'cancellationPenalties',
        'providerCode'
      ],
      pathOr({}, ['book', 'booking', 'response'], state)
    )
  )
}

export const getBookingInfo = ({
  state,
  searchParams
}: {
  state: StoreStateType
  searchParams: Record<string, unknown>
}) => {
  const bookingId = getBookingId(state)
  const manageBookingLink = pipe(
    pathOr([], ['book', 'booking', 'response', 'links']),
    find(propEq('type', ActionLinkTypes.MANAGE_BOOKING)),
    propOr(buildBookingManagementLink({...searchParams, bookingId}), 'href')
  )(state)

  const email = pathOr(
    '',
    ['book', 'booking', 'response', 'customer', 'email'],
    state
  )

  const merchantOfRecord = pathOr(
    '',
    ['book', 'booking', 'response', 'merchantOfRecord'],
    state
  )

  const customerSupport = pathOr(
    [],
    ['book', 'booking', 'response', 'customerSupport']
  )(state)

  return {
    confirmationId: getConfirmationId(state),
    manageBookingLink,
    customerSupport,
    email,
    merchantOfRecord
  }
}

export const getCountryCode = (state: StoreStateType) => {
  return pathOr(
    '',
    [
      'book',
      'booking',
      'request',
      'payment',
      'billing',
      'address',
      'countryCode'
    ],
    state
  )
}
